import { useState, useEffect, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { RefreshCw } from "lucide-react"
import { useToast } from "@/hooks/useToast"
import { useQueryClient } from "@tanstack/react-query"
import { useGetFieldPossibleValues, useUpdateField, useUpdateFieldPossibleValues } from "@/hooks/useResources"
import { ResourceField, ResourceContainer } from "@/types/api"
import { useSemanticTypes } from "@/hooks/useResources"

interface FieldSettingsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  field: ResourceField | null
  resourceId: string
  container: ResourceContainer | null
  onFieldUpdate: (
    fieldId: string,
    updates: {
      description: string
      display_name: string
      visibility: string
      data_type: string
      semantic_type: string
    }
  ) => void
}

export function FieldSettingsModal({
  open,
  onOpenChange,
  field,
  resourceId,
  container,
  onFieldUpdate,
}: FieldSettingsModalProps) {
  const [editingFields, setEditingFields] = useState<
    Record<
      string,
      {
        description: string
        display_name: string
        visibility: string
        data_type: string
        semantic_type: string
      }
    >
  >({})
  const [customMappingMode, setCustomMappingMode] = useState<"original" | "custom">("original")
  const [editingPossibleValues, setEditingPossibleValues] = useState<Record<string, string>>({})
  const [isResyncing, setIsResyncing] = useState(false)

  const { toast } = useToast()
  const queryClient = useQueryClient()
  const updateFieldMutation = useUpdateField()
  const updateFieldPossibleValuesMutation = useUpdateFieldPossibleValues()
  const { data: semanticTypes = [] } = useSemanticTypes()

  // Only fetch possible values if the field doesn't already have them configured
  const hasExistingPossibleValues = field?.source_meta?.possible_values && field.source_meta.possible_values.length > 0

  const { data: possibleValuesData, isLoading: possibleValuesLoading } = useGetFieldPossibleValues(
    resourceId,
    container?.id || "",
    field?.id || ""
  )

  // Merge fetched possible values with existing ones, preserving custom display names
  const mergedPossibleValues = useMemo(() => {
    if (!possibleValuesData || possibleValuesData.length === 0) {
      return field?.source_meta?.possible_values || []
    }

    // Create a map of existing values with their custom display names
    const existingValuesMap = new Map<string, { display_name?: string }>()
    if (field?.source_meta?.possible_values) {
      field.source_meta.possible_values.forEach((pv: any) => {
        existingValuesMap.set(String(pv.value), { display_name: pv.display_name })
      })
    }

    // Merge: use fetched data as source of truth for values and counts,
    // but preserve existing display_name if it was customized
    return possibleValuesData.map((pv: any) => {
      const existing = existingValuesMap.get(String(pv.value))
      return {
        ...pv,
        // Preserve custom display_name if it exists and is different from value
        display_name:
          existing?.display_name && existing.display_name !== String(pv.value)
            ? existing.display_name
            : pv.display_name || String(pv.value),
      }
    })
  }, [possibleValuesData, field?.source_meta?.possible_values])

  // Reset state when modal opens/closes or field changes
  useEffect(() => {
    if (!open || !field) {
      setEditingFields({})
      setCustomMappingMode("original")
      setEditingPossibleValues({})
      setIsResyncing(false)
    } else {
      // Set default mapping mode based on semantic type
      const currentSemanticType = field.semantic_type || "no_semantic_type"
      if (currentSemanticType === "category") {
        setCustomMappingMode("custom")
      } else {
        setCustomMappingMode("original")
      }
    }
  }, [open, field])

  const getFieldValue = (
    field: ResourceField,
    fieldType: "description" | "display_name" | "visibility" | "data_type" | "semantic_type"
  ) => {
    if (editingFields[field.id]) {
      return editingFields[field.id][fieldType]
    }
    return field[fieldType] || ""
  }

  const handleFieldChange = (
    fieldId: string,
    field: "description" | "display_name" | "visibility" | "data_type" | "semantic_type",
    value: string
  ) => {
    setEditingFields((prev) => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        [field]: value,
      },
    }))

    // Auto-switch to custom mapping when semantic type is set to category
    if (field === "semantic_type" && value === "category") {
      setCustomMappingMode("custom")
    } else if (field === "semantic_type" && value !== "category") {
      setCustomMappingMode("original")
    }
  }

  const isFieldEditing = (fieldId: string) => {
    return editingFields[fieldId] !== undefined || Object.keys(editingPossibleValues).length > 0
  }

  const handleSaveField = (fieldId: string) => {
    const fieldData = editingFields[fieldId]
    if (!fieldData || !field) return

    updateFieldMutation.mutate(
      {
        resourceId,
        containerId: container!.id,
        fieldId: fieldId,
        description: fieldData.description,
        display_name: fieldData.display_name,
        visibility: fieldData.visibility,
        data_type: fieldData.data_type,
        semantic_type: fieldData.semantic_type,
      },
      {
        onSuccess: () => {
          toast({
            title: "Field updated",
            description: "Field has been updated successfully.",
          })
          onFieldUpdate(fieldId, fieldData)
          queryClient
            .invalidateQueries({
              queryKey: ["resources/container-fields", resourceId, container!.id],
            })
            .finally(() => {
              setEditingFields((prev) => {
                const newState = { ...prev }
                delete newState[fieldId]
                return newState
              })
            })
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to update field. Please try again.",
            variant: "destructive",
          })
        },
      }
    )
  }

  const handleSave = () => {
    if (!field) return

    handleSaveField(field.id)

    // Save possible values if custom mapping is selected
    if (
      customMappingMode === "custom" &&
      (Object.keys(editingPossibleValues).length > 0 || mergedPossibleValues.length > 0)
    ) {
      // Use merged possible values which includes all fetched values
      if (mergedPossibleValues.length > 0) {
        const updatedPossibleValues = mergedPossibleValues.map((pv: any) => ({
          ...pv,
          display_name: editingPossibleValues[String(pv.value)] || pv.display_name || String(pv.value),
        }))

        updateFieldPossibleValuesMutation.mutate(
          {
            resourceId,
            containerId: container!.id,
            fieldId: field.id,
            possibleValues: updatedPossibleValues,
          },
          {
            onSuccess: () => {
              toast({
                title: "Possible values updated",
                description: "Field possible values have been updated successfully.",
              })
              // Refresh the fields to show updated possible values
              queryClient.invalidateQueries({
                queryKey: ["resources/container-fields", resourceId, container!.id],
              })
            },
            onError: () => {
              toast({
                title: "Error",
                description: "Failed to update possible values. Please try again.",
                variant: "destructive",
              })
            },
          }
        )
      }
    }

    onOpenChange(false)
  }

  if (!field) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-white">Field Settings</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Field Name */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Field Name</label>
            <Input
              value={getFieldValue(field, "display_name")}
              onChange={(e) => handleFieldChange(field.id, "display_name", e.target.value)}
              placeholder={field.field_name}
              className="w-full"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Original: {field.field_name}</p>
          </div>

          {/* Description */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <Input
              value={getFieldValue(field, "description")}
              onChange={(e) => handleFieldChange(field.id, "description", e.target.value)}
              placeholder="Add a description for this field..."
              className="w-full"
            />
          </div>

          {/* Semantic Type */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Semantic Type</label>
            <Select
              value={getFieldValue(field, "semantic_type") || field.semantic_type || "no_semantic_type"}
              onValueChange={(value) => handleFieldChange(field.id, "semantic_type", value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue>
                  {(() => {
                    const currentValue =
                      getFieldValue(field, "semantic_type") || field.semantic_type || "no_semantic_type"
                    const selectedType = semanticTypes.find((type) => type.value === currentValue)
                    return selectedType ? selectedType.label : "No semantic type"
                  })()}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-96">
                {Object.entries(
                  semanticTypes.reduce(
                    (groups, type) => {
                      if (!groups[type.category]) {
                        groups[type.category] = []
                      }
                      groups[type.category].push(type)
                      return groups
                    },
                    {} as Record<string, typeof semanticTypes>
                  )
                ).map(([category, types]) => (
                  <div key={category}>
                    <div className="bg-gray-50 px-2 py-1.5 text-xs font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                      {category}
                    </div>
                    {types.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2 py-1">
                          <div className="flex flex-col">
                            <span className="font-medium">{type.label}</span>
                            <span className="text-xs text-gray-500">{type.description}</span>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Visibility */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Visibility</label>
            <Select
              value={getFieldValue(field, "visibility") || "show"}
              onValueChange={(value) => handleFieldChange(field.id, "visibility", value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="show">Show</SelectItem>
                <SelectItem value="hide">Hide</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Display Values - Only show for category semantic type */}
          {(getFieldValue(field, "semantic_type") || field.semantic_type) === "category" && (
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">Display Values</label>
              <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                Choose to show the original value from the database, or have this field display custom mapped
                information.
              </p>
              <Select
                value={customMappingMode}
                onValueChange={(value: "original" | "custom") => setCustomMappingMode(value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="original">Use original value</SelectItem>
                  <SelectItem value="custom">Custom mapping</SelectItem>
                </SelectContent>
              </Select>

              {/* Custom mapping interface */}
              {customMappingMode === "custom" && (
                <div className="mt-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                  <div className="mb-4 flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">Value Mappings</h4>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isResyncing}
                      onClick={async () => {
                        if (!resourceId || !container || !field) return

                        setIsResyncing(true)
                        await queryClient
                          .invalidateQueries({
                            queryKey: ["resources/field-possible-values", resourceId, container.id, field.id],
                          })
                          .finally(() => {
                            toast({
                              title: "Values resynced",
                              description: "Possible values have been refreshed from the database.",
                            })
                            setIsResyncing(false)
                          })
                      }}
                    >
                      <RefreshCw className={`mr-2 h-4 w-4 ${isResyncing ? "animate-spin" : ""}`} />
                      {isResyncing ? "Resyncing..." : "Resync Values"}
                    </Button>
                  </div>

                  {(possibleValuesLoading && !hasExistingPossibleValues) || isResyncing ? (
                    <div className="py-8 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <RefreshCw className="h-4 w-4 animate-spin text-gray-500" />
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {isResyncing ? "Resyncing values..." : "Loading possible values..."}
                        </div>
                      </div>
                    </div>
                  ) : mergedPossibleValues && mergedPossibleValues.length > 0 ? (
                    <div className="space-y-2">
                      {mergedPossibleValues.map((possibleValue: any, index: number) => (
                        <div
                          key={`${possibleValue.value}-${index}`}
                          className="flex items-center space-x-3 rounded border border-gray-200 p-2 dark:border-gray-600"
                        >
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {String(possibleValue.value)}
                            </div>
                            <div className="text-xs text-gray-500">{possibleValue.count} occurrences</div>
                          </div>
                          <div className="flex-1">
                            <Input
                              placeholder="Enter display name"
                              value={
                                editingPossibleValues[String(possibleValue.value)] ||
                                possibleValue.display_name ||
                                String(possibleValue.value)
                              }
                              onChange={(e) => {
                                setEditingPossibleValues((prev) => ({
                                  ...prev,
                                  [String(possibleValue.value)]: e.target.value,
                                }))
                              }}
                              className="h-8 text-sm"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 text-center">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {hasExistingPossibleValues
                          ? "No possible values configured for this field."
                          : 'No possible values found. Click "Resync Values" to fetch values from the resource.'}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 border-t border-gray-200 pt-4 dark:border-gray-700">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!isFieldEditing(field.id)}>
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
